// FILE: src/components/ui/AgentCard.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { VisualElementV10 } from '@dopomogai/types';
import { renderIcon } from '@dopomogai/utils';
import { Badge } from './Badge';

// Lazy load the 3D component for performance

// --- Type Definitions ---

type AgentAccentColor = 'sky' | 'emerald' | 'amber' | 'rose' | 'violet' | 'slate';

export interface AgentCardBadge {
  text?: string;
  textKey?: string;
  icon?: string | React.ReactNode;
  accentColor?: 'blue' | 'green' | 'purple' | 'red' | 'orange' | 'slate' | 'yellow' | 'pink' | 'cyan' | 'gray' | 'indigo' | 'teal' | 'lime' | 'fuchsia' | 'violet';
}

export interface AgentCardData {
  id: string;
  slug?: string;
  name: string;
  tagline: string;
  promoBadgeText?: string; // Field for the promotional badge
  visual: VisualElementV10;
  accentColor?: AgentAccentColor;
  badges?: AgentCardBadge[];
  creatorName?: string;
  version?: string;
  highlightMetric?: {
    icon?: string | React.ReactNode;
    value: string;
    label: string;
    labelKey?: string;
  };
  impactSnippet?: {
    beneficiary: string;
    beneficiaryKey?: string;
    icon?: string | React.ReactNode;
  };
  ctaText?: string;
  ctaTextKey?: string;
  onCardClick?: () => void;
  learnMoreUrl?: string;
}

interface AgentCardProps {
  data: AgentCardData;
  variant?: 'default' | 'compact' | 'featured';
  className?: string;
  onAction?: (
    actionType: 'OPEN_URL' | 'NAVIGATE_TO_AGENT',
    payload: { url: string } | { slug: string }
  ) => void;
  showImpactSnippet?: boolean;
  maxBadges?: number;
}

// --- Color & Style Mappings ---

const accentColorClasses: Record<AgentAccentColor, { gradient: string; metricBg: string; impactText: string }> = {
  sky: {
    gradient: 'from-sky-500/20',
    metricBg: 'bg-sky-500/90 [.theme-liquid-glass_&]:bg-sky-400/80',
    impactText: 'text-sky-600 [.theme-liquid-glass_&]:text-sky-200',
  },
  emerald: {
    gradient: 'from-emerald-500/20',
    metricBg: 'bg-emerald-500/90 [.theme-liquid-glass_&]:bg-emerald-400/80',
    impactText: 'text-emerald-600 [.theme-liquid-glass_&]:text-emerald-200',
  },
  amber: {
    gradient: 'from-amber-500/20',
    metricBg: 'bg-amber-500/90 [.theme-liquid-glass_&]:bg-amber-400/80',
    impactText: 'text-amber-600 [.theme-liquid-glass_&]:text-amber-200',
  },
  rose: {
    gradient: 'from-rose-500/20',
    metricBg: 'bg-rose-500/90 [.theme-liquid-glass_&]:bg-rose-400/80',
    impactText: 'text-rose-600 [.theme-liquid-glass_&]:text-rose-200',
  },
  violet: {
    gradient: 'from-violet-500/20',
    metricBg: 'bg-violet-500/90 [.theme-liquid-glass_&]:bg-violet-400/80',
    impactText: 'text-violet-600 [.theme-liquid-glass_&]:text-violet-200',
  },
  slate: {
    gradient: 'from-slate-500/20',
    metricBg: 'bg-slate-500/90 [.theme-liquid-glass_&]:bg-slate-400/80',
    impactText: 'text-slate-600 [.theme-liquid-glass_&]:text-slate-200',
  },
};

/**
 * A responsive, accessible, and feature-rich card component to display an "Agent".
 * The entire card is a clickable element.
 */
export const AgentCard: React.FC<AgentCardProps> = ({
  data,
  variant = 'default',
  className,
  onAction,
  showImpactSnippet = true,
  maxBadges = 3,
}) => {
  const { t } = useTranslation();

  const displayBadges = data.badges?.slice(0, maxBadges) || [];
  const isClickable = !!(data.onCardClick || data.slug || data.learnMoreUrl);
  const accent = accentColorClasses[data.accentColor || 'slate'];

  const variantClasses = {
    compact: { visual: 'aspect-[4/3]', content: 'p-4', name: 'text-lg', tagline: 'text-sm mb-4' },
    featured: { visual: 'aspect-[16/10]', content: 'p-6', name: 'text-xl', tagline: 'text-base mb-5' },
    default: { visual: 'aspect-[4/3]', content: 'p-5', name: 'text-lg', tagline: 'text-sm mb-4' },
  }[variant];

  const handleCardClick = () => {
    // Defines a clear priority for click actions.
    // PRIORITY 1: A specific URL is the most direct instruction.
    if (data.learnMoreUrl) {
      onAction?.('OPEN_URL', { url: data.learnMoreUrl });
    }
    // PRIORITY 2: A custom callback function.
    else if (data.onCardClick) {
      data.onCardClick();
    }
    // PRIORITY 3: Fallback to slug-based navigation if no URL is provided.
    else if (data.slug) {
      onAction?.('NAVIGATE_TO_AGENT', { slug: data.slug });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={clsx('relative group', className)}
      {...(isClickable && {
        onClick: handleCardClick,
        onKeyDown: handleKeyDown,
        role: 'button',
        tabIndex: 0,
        'aria-label': t('a11y.viewDetailsFor', 'View details for {{name}}', { name: data.name }),
      })}
    >
      {isClickable && <div className={clsx(
        'absolute inset-0 rounded-xl',
        // Neobrutalism shadow element
        'bg-black',
        // Liquid glass - hide shadow
        '[.theme-liquid-glass_&]:hidden'
      )} />}

      <div
        className={clsx(
          'relative rounded-xl overflow-hidden flex flex-col h-full',
          // Neobrutalism
          'bg-white border-2 border-black',
          // Liquid glass
          '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
          '[.theme-liquid-glass_&]:backdrop-blur-xl [.theme-liquid-glass_&]:rounded-3xl',
          isClickable && 'transition-transform duration-200 ease-out transform group-hover:-translate-x-1 group-hover:-translate-y-1 group-active:translate-x-0 group-active:translate-y-0 cursor-pointer',
          isClickable && '[.theme-liquid-glass_&]:group-hover:translate-x-0 [.theme-liquid-glass_&]:group-hover:-translate-y-1'
        )}
      >
        <div className={clsx(
          'relative overflow-hidden bg-slate-100 border-b-2 border-black',
          '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/10',
          variantClasses.visual,
          'bg-gradient-to-t to-transparent',
          accent.gradient
        )}>

          {/* --- CHANGE #1: ADDED THE PROMOTIONAL BADGE --- */}
          {data.promoBadgeText && (
            <div
              className={clsx(
                'absolute top-4 -right-1 z-20 transform rotate-12',
                'rounded-md border-2 border-black bg-amber-300 px-3 py-1',
                'text-xs font-bold uppercase tracking-wider text-black shadow-[2px_2px_0_0_#000]'
              )}
            >
              {data.promoBadgeText}
            </div>
          )}

          {data.visual.type === 'image' && (
            <img
              src={data.visual.url}
              alt={data.visual.altText || `Visual for ${data.name}`}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          )}

          <div className="absolute inset-0 z-10">
            {data.version && (
              <div className="absolute top-3 left-3">
                <span className="bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-semibold px-2 py-1 rounded-full border border-slate-300 shadow-sm [.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:text-white/80">
                  v{data.version}
                </span>
              </div>
            )}
            {data.highlightMetric && (
              <div className="absolute top-3 right-3">
                <div className={clsx('backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1.5 rounded-lg border-2 border-black shadow-sm flex items-center gap-1.5 [.theme-liquid-glass_&]:border-white/20', accent.metricBg)}>
                  {renderIcon(data.highlightMetric.icon, { size: 12 })}
                  <span>{data.highlightMetric.value}</span>
                </div>
              </div>
            )}
          </div>
        </div>

          <div className={clsx('flex flex-col flex-grow', variantClasses.content)}>
            <div className="flex-grow">
            <h3 className={clsx('font-bold text-gray-900 leading-tight mb-1 [.theme-liquid-glass_&]:text-white', variantClasses.name)}>{data.name}</h3>
            <p className={clsx('text-slate-600 leading-relaxed [.theme-liquid-glass_&]:text-white/70', variantClasses.tagline)}>{data.tagline}</p>
            {displayBadges.length > 0 && (
              <div className="flex flex-col items-start gap-2 mt-4">
                {displayBadges.map((badge, index) => (
                  <Badge
                    key={`${data.id}-badge-${index}`}
                    text={badge.text}
                    textKey={badge.textKey}
                    icon={badge.icon}
                    accentColor={badge.accentColor}
                    className="text-sm"
                  />
                ))}
              </div>
            )}
          </div>

          <div className="mt-auto pt-4 border-t border-slate-200 [.theme-liquid-glass_&]:border-white/10">
            <div className="flex items-center justify-between text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/60">
              {data.creatorName && (
                <span className="flex items-center gap-1">
                  <span>{t('by', 'By')}</span>
                  <span className="font-semibold text-slate-700 [.theme-liquid-glass_&]:text-white/80">{data.creatorName}</span>
                </span>
              )}
              {showImpactSnippet && data.impactSnippet && (
                <span className={clsx('flex items-center gap-1', accent.impactText)}>
                  {renderIcon(data.impactSnippet.icon, { size: 12 })}
                  <span className="font-medium">
                    {data.impactSnippet.beneficiaryKey ? t(data.impactSnippet.beneficiaryKey) : data.impactSnippet.beneficiary}
                  </span>
                </span>
              )}
            </div>

            {/* --- CHANGE #2: REMOVED THE BUTTON --- */}
            {/* The line below that rendered the Button component has been deleted. */}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
